import type { CreditInputs, CreditProduct } from './useCreditSimulator';
import type { FormMortgageConditions } from '~/types/mixpanel';

import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';

import { useCreditSimulator } from './useCreditSimulator';

const TAB_NAMES: Record<
	CreditProduct,
	FormMortgageConditions[MixpanelProps.TAB]
> = {
	financing: 'Financiar',
	consortium: 'Consórcio',
	homeEquity: 'CGI'
};

// Typed amounts turn the percentage into a long fraction.
function roundPercent(value: number): number {
	return Math.round(value * 100) / 100;
}

function percentOf(value: number, percent: number): number {
	return Math.round((value * percent) / 100);
}

export function buildMortgageConditions(
	product: CreditProduct,
	inputs: CreditInputs,
	formHash: string
): FormMortgageConditions {
	const base = {
		[MixpanelProps.FORM_HASH]: formHash,
		[MixpanelProps.TAB]: TAB_NAMES[product]
	};

	if (product === 'financing') {
		return {
			...base,
			[MixpanelProps.PROPERTY_VALUE]: inputs.financingValue,
			[MixpanelProps.DOWN_PAYMENT_VALUE]: percentOf(
				inputs.financingValue,
				inputs.financingDownPaymentPercent
			),
			[MixpanelProps.DOWN_PAYMENT_PERCENT]: roundPercent(
				inputs.financingDownPaymentPercent
			),
			[MixpanelProps.LOAN_TERM_MONTHS]: inputs.financingYears * 12
		};
	}

	if (product === 'consortium') {
		return {
			...base,
			[MixpanelProps.CREDIT_LETTER_VALUE]: inputs.consortiumLetter,
			[MixpanelProps.LOAN_TERM_MONTHS]: inputs.consortiumMonths
		};
	}

	return {
		...base,
		[MixpanelProps.PROPERTY_VALUE]: inputs.homeEquityValue,
		[MixpanelProps.LOAN_AMOUNT]: percentOf(
			inputs.homeEquityValue,
			inputs.homeEquityPercent
		),
		[MixpanelProps.LOAN_AMOUNT_PERCENT]: roundPercent(inputs.homeEquityPercent),
		[MixpanelProps.LOAN_TERM_MONTHS]: inputs.homeEquityMonths
	};
}

// Fired from the step buttons, never from watchers, so each click counts once.
export function useCreditEvents() {
	const simulator = useCreditSimulator();
	const state = simulator.state;
	const {
		trackFormMortgageConditions,
		trackFormMortgageQualification,
		trackFormMortgageCompleted
	} = useEvents();

	function trackConditions() {
		trackFormMortgageConditions(
			buildMortgageConditions(
				state.value.product,
				state.value.inputs,
				simulator.ensureFormHash()
			)
		);
	}

	function trackQualification() {
		trackFormMortgageQualification({
			[MixpanelProps.FORM_HASH]: simulator.ensureFormHash(),
			[MixpanelProps.CREDIT_FINALITY]: state.value.intent,
			[MixpanelProps.FAMILY_INCOME]: state.value.income
		});
	}

	function trackCompleted(contact: {
		name?: string;
		phone: string;
		email?: string;
	}) {
		trackFormMortgageCompleted({
			[MixpanelProps.FORM_HASH]: simulator.ensureFormHash(),
			[MixpanelProps.INPUT_NAME]: contact.name ?? '',
			[MixpanelProps.INPUT_PHONE]: contact.phone,
			[MixpanelProps.INPUT_EMAIL]: contact.email || ''
		});
	}

	return { trackConditions, trackQualification, trackCompleted };
}
